export function format_cents(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(Number(cents))) return ''
  const value = Number(cents) / 100
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: value < 1 ? 0 : 2 })
}

/**
 * Fuso do relógio que grava os carimbos.
 *
 * O MySQL deste projeto roda em `America/Sao_Paulo` (`-e TZ=America/Sao_Paulo` no
 * `docker run`, `TZ` no compose) e grava `DATETIME` **sem fuso**: o valor na
 * coluna é hora de Brasília. Fixo em `-03:00` porque o Brasil aboliu o horário
 * de verão em 2019 — o deslocamento não varia mais.
 *
 * Não se usa o fuso do navegador: ele pode estar em qualquer lugar, enquanto o
 * carimbo é sempre do relógio do banco.
 */
const DB_UTC_OFFSET = '-03:00'

/**
 * Converte o `DATETIME` cru do banco em `Date`.
 *
 * O defeito que isto corrige: a versão anterior anexava `'Z'`, declarando hora
 * de Brasília como UTC e jogando TODA data relativa **3 horas para trás** — em
 * 12 chamadas, cinco telas, incluindo a home pública e o detalhe do anúncio, que
 * mostravam "publicado há 3 h" para um anúncio recém-publicado.
 *
 * Valor que já traz fuso (ISO com `Z` ou com offset) passa intacto: se um dia a
 * API devolver ISO, não pode levar `-03:00` em cima do que já tem.
 */
export function parse_db_datetime(value: string): Date {
  const texto = String(value).trim()
  const ja_tem_fuso = /(?:Z|[+-]\d{2}:?\d{2})$/.test(texto)
  const iso = texto.replace(' ', 'T')
  return new Date(ja_tem_fuso ? iso : iso + DB_UTC_OFFSET)
}

export function relative_date(value: string | null | undefined): string {
  if (!value) return ''
  const date = parse_db_datetime(value)
  if (Number.isNaN(date.getTime())) return ''
  // Carimbo alguns segundos no futuro (relógio do navegador adiantado) não pode
  // virar "há -1 min": o piso em zero é o que torna "agora" o caso correto.
  const diff_ms = Math.max(0, Date.now() - date.getTime())
  const diff_min = Math.floor(diff_ms / 60_000)
  if (diff_min < 1) return 'agora'
  if (diff_min < 60) return `há ${diff_min} min`
  const diff_h = Math.floor(diff_min / 60)
  if (diff_h < 24) return `há ${diff_h} h`
  const diff_d = Math.floor(diff_h / 24)
  if (diff_d < 7) return `há ${diff_d} d`
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' }).format(date)
}
